import GhostContentAPI from '@tryghost/content-api'
import type { GhostAPI } from '@tryghost/content-api'

/**
 * A collection of methods to call the Ghost API endpoints with.
 */
let ghostClient: Nullable<GhostAPI> = null

export default function useGhostApi() {
  const runtimeConfig = useRuntimeConfig()
  const { locale } = useI18n({ useScope: 'global' })

  /**
   * Gets the Ghost client instance.
   *
   * @returns The Ghost client instance
   */
  function getGhostClient() {
    ghostClient ??= new GhostContentAPI({
      url: runtimeConfig.public.blog.ghostApiUrl,
      key: runtimeConfig.public.blog.ghostApiKey,
      version: 'v6.0',
    })

    return ghostClient
  }

  /**
   * Fetches all posts from the Ghost CMS.
   *
   * @param page The page number to fetch
   * @param limit The limit of posts to fetch
   * @param tag The tag to fetch the posts by
   * @param author The author to fetch the posts by
   * @param scopeToLocale Restrict results to posts tagged with the active locale.
   * Pass `false` when the tag is already locale specific, since those posts are
   * not guaranteed to also carry a locale tag.
   * @returns A promise that resolves when the posts are fetched
   */
  async function getPosts({ page = 1, limit = 10, tag, author, scopeToLocale = true }: { page?: number, limit?: number, tag?: string, author?: string, scopeToLocale?: boolean }) {
    const filter = []
    // Blog posts carry a tag matching their locale, so this keeps results in the
    // active language even when a tag or author slug is shared across locales.
    if (scopeToLocale) {
      filter.push(`tags:${locale.value}`)
    }
    if (tag) {
      filter.push(`tag:${tag}`)
    }
    if (author) {
      filter.push(`primary_author:${author}`)
    }

    const results = await getGhostClient().posts.browse({
      page: page ?? undefined,
      limit,
      include: ['tags', 'authors'],
      filter: filter.length ? filter.join('+') : undefined,
    })

    // Ghost hands back an array with `meta` glued onto it. Properties attached
    // to an array don't survive Nuxt's payload serialization, so the
    // meta data has to live on a plain object to remain available after
    // hydration.
    return {
      posts: Array.from(results ?? []),
      meta: results?.meta ?? null,
    }
  }

  /**
   * Fetches a single post from the Ghost CMS.
   *
   * @param slug The slug of the post to fetch
   * @param id The id of the post to fetch
   * @returns A promise that resolves when the post is fetched
   */
  function getPost(slug: string) {
    return getGhostClient().posts.read(
      { slug },
      { include: ['tags', 'authors'], formats: ['html'] },
    )
  }

  function getPage(id: string) {
    return getGhostClient().pages.read(
      { id },
      { formats: ['html'] },
    )
  }

  /**
   * Fetches all tags from the Ghost CMS.
   *
   * @returns A promise that resolves when the tags are fetched
   */
  async function getTags() {
    const results = await getGhostClient().tags.browse({
      include: 'count.posts',
      limit: 'all',
      filter: 'visibility:public',
      order: 'count.posts DESC',
    })

    return Array.from(results ?? [])
  }

  return {
    getPage,
    getPosts,
    getPost,
    getTags,
  }
}
